import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RecentlyViewed from './RecentlyViewed';
import { Helmet } from 'react-helmet';
import Spinner from './Components/Spinner';
import { useAuth } from './AuthContext';
import CarCard from './Components/CarCard';
import SaveSearchModal from './SaveSearchModal';
import { Link } from 'react-router-dom';

function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const initialMake = searchParams.get('make') || '';
  const initialModel = searchParams.get('model') || '';
  const initialRadius = searchParams.get('radius') || '25';
  const initialZip = searchParams.get('zip') || '';
  const initialMinYear = searchParams.get('minYear') || '';
  const initialMaxYear = searchParams.get('maxYear') || '';
  const initialMinPrice = searchParams.get('minPrice') || '';
  const initialMaxPrice = searchParams.get('maxPrice') || '';
  const initialSort = searchParams.get('sort') || '';
  const initialPage = parseInt(searchParams.get('page') || '1');

  const [make, setMake] = useState(initialMake);
  const [model, setModel] = useState(initialModel);
  const [radius, setRadius] = useState(initialRadius);
  const [zip, setZip] = useState(initialZip);
  const [minYear, setMinYear] = useState(initialMinYear);
  const [maxYear, setMaxYear] = useState(initialMaxYear);
  const [minPrice, setMinPrice] = useState(initialMinPrice);
  const [maxPrice, setMaxPrice] = useState(initialMaxPrice);
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(initialPage);

  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [cars, setCars] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trim, setTrim] = useState(searchParams.get('trim') || '');
const [trimOptions, setTrimOptions] = useState([]);
const { user } = useAuth();
const [savedCarIds, setSavedCarIds] = useState([]);
const [showModal, setShowModal] = useState(false);
const [maxMileage, setMaxMileage] = useState(searchParams.get('maxMileage') || '');

const [filters, setFilters] = useState({
  zip: '',
  makeName: '',  // <-- must be included here
  model: '',
  minYear: '',
  maxYear: '',
  minPrice: '',
  maxPrice: '',
  maxDistance: ''
});


useEffect(() => {
  if (!user?.id) return;

  fetch(`${process.env.REACT_APP_API_BASE}/saved/${user.id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  })
    .then(res => res.json())
    .then(data => setSavedCarIds(data))
    .catch(err => console.error('Failed to fetch saved cars:', err));
}, [user]);

  useEffect(() => {
    setPage(parseInt(new URLSearchParams(location.search).get("page") || "1"));
  }, [location.search]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/makes`)
      .then(res => res.json())
      .then(data => setMakeOptions(data));
  }, []);

  useEffect(() => { setTrim(''); }, [make, model]);

  useEffect(() => {
    if (make) {
      fetch(`${process.env.REACT_APP_API_BASE}/cars/models?make=${encodeURIComponent(make)}`)
        .then(res => res.json())
        .then(data => setModelOptions(data));
    } else {
      setModelOptions([]);
    }
  }, [make]);

  useEffect(() => {
  const cachedCars = sessionStorage.getItem('cachedCars');
  const cachedPage = sessionStorage.getItem('cachedPage');
  const cachedScroll = sessionStorage.getItem('cachedScroll');
  const currentPage = new URLSearchParams(location.search).get('page') || '1';

  if (cachedCars && cachedPage === currentPage) {
    setCars(JSON.parse(cachedCars));
    setTotalPages(parseInt(sessionStorage.getItem('cachedTotalPages') || '1'));
    setTotalResults(parseInt(sessionStorage.getItem('cachedTotalResults') || '0'));
    setLoading(false);
    // Restore scroll after short delay
    setTimeout(() => {
      window.scrollTo(0, parseInt(cachedScroll || '0'));
    }, 0);
  } else {
    const params = new URLSearchParams(location.search);
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE}/cars/active?${params.toString()}`)
      .then(res => res.json())
      .then(data => {
        setCars(data?.results || []);
        setTotalPages(data?.totalPages || 1);
        setTotalResults(data?.totalResults || 0);
        // Cache values
        sessionStorage.setItem('cachedCars', JSON.stringify(data?.results || []));
        sessionStorage.setItem('cachedTotalPages', data?.totalPages?.toString() || '1');
        sessionStorage.setItem('cachedTotalResults', data?.totalResults?.toString() || '0');
        sessionStorage.setItem('cachedPage', currentPage);
        sessionStorage.setItem('cachedScroll', '0');
      })
      .catch(() => {
        setCars([]);
        setTotalPages(1);
        setTotalResults(0);
      })
      .finally(() => setLoading(false));
  }
}, [location.search]);

useEffect(() => {
  const params = new URLSearchParams(location.search);
  setMake(params.get('make') || '');
  setModel(params.get('model') || '');
  setRadius(params.get('radius') || '25');
  setZip(params.get('zip') || '');
  setMinYear(params.get('minYear') || '');
  setMaxYear(params.get('maxYear') || '');
  setMinPrice(params.get('minPrice') || '');
  setMaxPrice(params.get('maxPrice') || '');
  setSort(params.get('sort') || '');
  setTrim(params.get('trim') || '');
}, [location.search]);

useEffect(() => {
  handleSearchClick();
  // Trigger search only when URL changes (like from a saved search click)
}, [location.search]);

useEffect(() => {
  if (make && model) {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/trims?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`)
      .then(res => res.json())
      .then(data => setTrimOptions(data))
      .catch(() => setTrimOptions([]));
  } else {
    setTrimOptions([]);
  }
}, [make, model]);

  const handleSearchClick = () => {
    const query = new URLSearchParams();
    if (make) query.append('make', make);
    if (model) query.append('model', model);
    if (radius) query.append('radius', radius);
    if (zip) query.append('zip', zip);
    if (minYear) query.append('minYear', minYear);
    if (maxYear) query.append('maxYear', maxYear);
    if (minPrice) query.append('minPrice', minPrice);
    if (maxPrice) query.append('maxPrice', maxPrice);
    if (sort) query.append('sort', sort);
    if (trim) query.append('trim', trim);
    if (maxMileage) query.append('maxMileage', maxMileage);

    query.append('page', '1');
              sessionStorage.setItem('cachedCars',  []);
        sessionStorage.setItem('cachedTotalPages',  '0');
        sessionStorage.setItem('cachedTotalResults', '0');
        sessionStorage.setItem('cachedPage', "");
        sessionStorage.setItem('cachedScroll', '0');
    navigate(`/search?${query.toString()}`);
  };
const handleCarClick = (car) => {
  sessionStorage.setItem('cachedCars', JSON.stringify(cars));
  sessionStorage.setItem('cachedPage', page);
  sessionStorage.setItem('scrollPos', window.scrollY);
  navigate(`/car/${car.id}`);
};

  const goToPage = (newPage) => {
    const params = new URLSearchParams(location.search);
    params.set('page', newPage);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <>
      <Helmet>
        <title>
          {make && model ? `${make} ${model} cars for sale` : make ? `${make} cars for sale` : 'Used Cars for Sale'} | Meshum
        </title>
        <meta name="description" content={make && model ? `Explore listings of ${make} ${model} cars with pricing, mileage, and dealer info.` : make ? `See all available ${make} vehicles near you with pricing and features.` : 'Search used cars for sale near you. Compare prices with recently sold vehicles on Meshum.'} />
      </Helmet>

      <div style={{ padding: "2rem", backgroundColor: "#f9f9f9" }}>
        <h2 style={{ fontSize: "2rem", marginBottom: "1rem", color: "#004D40", textAlign: "center" }}>Search Results</h2>

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
  <button
    onClick={() => setShowFilterModal(true)}
    style={{
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      backgroundColor: '#00796B',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      marginRight: user?.id ? '1rem' : '0'
    }}
  >
    Show Filters
  </button>

  {user?.id && (
    <>
      <button
        onClick={() => {
          setFilters({
            zip,
            makeName: make,
            model,
            minYear,
            maxYear,
            minPrice,
            maxPrice,
            maxDistance: radius,
            sort,
            maxMileage
          });
          setShowModal(true);
        }}
        style={{
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          backgroundColor: '#00796B',
          color: '#fff',
          border: 'none',
          borderRadius: '6px'
        }}
      >
        Save This Search
      </button>

      <div style={{ marginTop: '0.75rem' }}>
        <Link
          to="/savedsearches"
          style={{
            fontSize: '0.9rem',
            color: '#00796B',
            textDecoration: 'underline'
          }}
        >
          View Saved Searches
        </Link>
      </div>

      <SaveSearchModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSaved={() => { /* optional reload */ }}
        currentFilters={filters}
        userId={user.id}
      />
    </>
  )}
</div>







        {showFilterModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '10px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0 }}>Filters</h3>
              <input placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }} />
              <select value={radius} onChange={(e) => setRadius(e.target.value)} style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }}>
                <option value="5">5 miles</option>
                <option value="10">10 miles</option>
                <option value="25">25 miles</option>
                <option value="50">50 miles</option>
                <option value="100">100 miles</option>
                <option value="250">250 miles</option>
                <option value="9999">Nationwide</option>
              </select>
              <select value={make} onChange={(e) => { setMake(e.target.value); setModel('') }} style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }}>
                {/* <option value="">All Makes</option> */}
                {makeOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={model} onChange={(e) => setModel(e.target.value)} disabled={!make} style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }} >
                {/* No default option – just the actual models */}
                <option value="" disabled >
                {make ? 'Select a model' : 'Select a make first'}
              </option>
                {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={trim} onChange={(e) => setTrim(e.target.value)} style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }}>
                <option value="">All Trims</option>
                {trimOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
  value={maxMileage}
  onChange={(e) => setMaxMileage(e.target.value)}
  style={{ padding: '0.75rem', marginBottom: '1rem', width: '100%' }}
>
  <option value="">Max Mileage</option>
  <option value="15000">Less than 15,000 miles</option>
  <option value="30000">Less than 30,000 miles</option>
  <option value="45000">Less than 45,000 miles</option>
  <option value="60000">Less than 60,000 miles</option>
  <option value="80000">Less than 80,000 miles</option>
  <option value="999999">All mileages</option>
</select>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <input type="number" placeholder="Min Year" value={minYear} onChange={(e) => setMinYear(e.target.value)} style={{ flex: 1, padding: '0.75rem' }} />
                <input type="number" placeholder="Max Year" value={maxYear} onChange={(e) => setMaxYear(e.target.value)} style={{ flex: 1, padding: '0.75rem' }} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                <input type="number" placeholder="Min Price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} style={{ flex: 1, padding: '0.75rem' }} />
                <input type="number" placeholder="Max Price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} style={{ flex: 1, padding: '0.75rem' }} />
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: '0.75rem', marginBottom: '1.5rem', width: '100%' }}>
                <option value="">Sort By</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="year_desc">Year: Newest</option>
                <option value="year_asc">Year: Oldest</option>
                <option value="mileage_asc">Mileage: Low to High</option>
              </select>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setShowFilterModal(false)} style={{ padding: '0.6rem 1.2rem', backgroundColor: '#ccc', border: 'none', borderRadius: '4px' }}>Cancel</button>
                <button  disabled={!make || !model} onClick={() => { handleSearchClick(); setShowFilterModal(false); }} style={{ padding: '0.6rem 1.2rem', backgroundColor: '#00796B', color: '#fff', border: 'none', borderRadius: '4px' }}>Apply</button>
              </div>
            </div>
          </div>
        )}


        {loading ? (
          <Spinner />
        ) : (
          <>
           <h3 style={{ margin: "1rem 0", color: "#444" }}>
  Showing {(page - 1) * 20 + 1}–{(page - 1) * 20 + cars.length} of {totalResults} {totalResults === 1 ? "car" : "cars"}
</h3>


            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.5rem" }}>
{cars.map(car => (
<CarCard key={car.id} car={car} loggedInUser={user} saved={savedCarIds.includes(car.id)} onClick={handleCarClick} />

))}
              {/* {cars.map(car => (
                <div key={car.id} onClick={() => handleCarClick(car)} style={{ border: "1px solid #0097A7", padding: "1rem", borderRadius: "8px", backgroundColor: "#fff", boxShadow: "0 2px 10px rgba(0, 151, 167, 0.15)", cursor: "pointer", transition: "transform 0.2s" }}>
                  <img src={car.imageUrl} alt="Car" style={{ width: "100%", borderRadius: "6px", marginBottom: "0.5rem" }} />
                  <h4 style={{ margin: "0.25rem 0" }}>{car.year} {car.make} {car.model} {car.trim}</h4>
                  <p style={{ fontWeight: 'bold', color: '#004D40' }}>${car.price.toLocaleString()}</p>
                  <p style={{ color: '#666' }}>{car.mileage.toLocaleString()} miles</p>
                  <p style={{ color: '#888' }}>{car.dealerCity}, {car.dealerState}</p>
                  {car.priceLabel && (
                    <div style={{ marginTop: "0.5rem", display: "inline-block", padding: "0.3rem 0.6rem", borderRadius: "999px", fontWeight: "bold", fontSize: "0.85rem", backgroundColor: car.priceLabel === "Great Deal" ? "#4caf50" : car.priceLabel === "Fair Price" ? "#ffc107" : car.priceLabel === "Overpriced" ? "#f44336" : "#ccc", color: car.priceLabel === "Fair Price" ? "#000" : "#fff" }}>{car.priceLabel}</div>
                  )}
                </div>
              ))} */}
            </div>
          </>
        )}

        {totalPages > 1 && !loading && (
          <div style={{ marginTop: "2rem", textAlign: "center" }}>
            <button onClick={() => goToPage(page - 1)} disabled={page <= 1} style={{ padding: "0.5rem 1rem", backgroundColor: "#0097A7", color: "#fff", border: "none", borderRadius: "4px", marginRight: "1rem" }}>Prev</button>
            <span style={{ fontWeight: "bold" }}>Page {page} of {totalPages}</span>
            <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} style={{ padding: "0.5rem 1rem", backgroundColor: "#0097A7", color: "#fff", border: "none", borderRadius: "4px", marginLeft: "1rem" }}>Next</button>
          </div>
        )}

        <RecentlyViewed cars={JSON.parse(localStorage.getItem('recentlyViewedCars') || '[]')} />
      </div>
    </>
  );
}

export default SearchResults;
